import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { switchMap, map, startWith, tap } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { OrganizationUnit, MemberLastAttendance } from '../models/unit.model';
import { UnitService } from '../services/unit.service';
import { UnitLastAttendanceCardComponent } from '../components/unit-last-attendance-card.component';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { UserFullInfoDto } from '@shared/models/user-full-info.model';
import { UnitHomeAttendanceCardComponent } from '../components/unit-home-attendance-card.component';

interface UnitPageViewModel {
  unit: OrganizationUnit;
  canViewData: boolean;
  lastAttendance: LoadState<MemberLastAttendance[]>;
}

interface LoadState<T> {
  data: T;
  isLoading: boolean;
  errorMessage: string | null;
}

function loaded<T>(data: T): LoadState<T> {
  return { data, isLoading: false, errorMessage: null };
}

function loading<T>(data: T): LoadState<T> {
  return { data, isLoading: true, errorMessage: null };
}

function failed<T>(data: T, error: unknown): LoadState<T> {
  return { data, isLoading: false, errorMessage: getLoadErrorMessage(error) };
}

function getLoadErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse && error.status === 408) {
    return 'La consulta tardó demasiado. Probá nuevamente o abrí el listado completo.';
  }

  return 'No se pudo cargar esta información.';
}

@Component({
  selector: 'app-unit-home-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    UnitLastAttendanceCardComponent,
    UnitHomeAttendanceCardComponent,
  ],
  templateUrl: './unit-home-page.component.html',
  styleUrl: './unit-home-page.component.scss'
})
export class UnitHomePageComponent {
  readonly vm$: Observable<UnitPageViewModel>;
  private readonly lastAttendanceReload$ = new Subject<void>();

  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly userFullInfoService = inject(UserFullInfoService);
  readonly hasActivity$ = this.userFullInfoService.hasActivity$;


  constructor(route: ActivatedRoute, unitService: UnitService) {
    this.vm$ = combineLatest([
      route.parent!.parent!.paramMap,
      this.userFullInfoService.getUserInfo$(),
    ]).pipe(
      switchMap(([paramMap, user]) => {
        const unitId = paramMap.get('unit-id')!;

        // El unit se carga primero y emite inmediatamente cuando está disponible
        return unitService.getUnit(unitId).pipe(
          tap(unit => this.breadcrumbService.setDynamicCrumb('unit-id', unit.name)),
          switchMap(unit => {
            const canViewData = this.canViewUnitData(unitId, user);

            if (!canViewData) {
              return of({
                unit,
                canViewData,
                lastAttendance: loaded([] as MemberLastAttendance[]),
              });
            }

            const lastAttendance$ = this.lastAttendanceReload$.pipe(
              startWith(void 0),
              switchMap(() => unitService.getMemberLastAttendance(unitId, 100).pipe(
                map(data => loaded(data)),
                startWith(loading([] as MemberLastAttendance[])),
                catchError(error => of(failed([] as MemberLastAttendance[], error)))
              ))
            );

            return lastAttendance$.pipe(
              map(lastAttendance => ({
                unit,
                canViewData,
                lastAttendance,
              }))
            );
          })
        );
      })
    );
  }

  retryLastAttendance(): void {
    this.lastAttendanceReload$.next();
  }

  private canViewUnitData(unitId: string, user: UserFullInfoDto | null): boolean {
    if (!user) {
      return false;
    }

    if (user.isAdmin) {
      return true;
    }

    return user.readableUnitIds.some(readableUnitId =>
      readableUnitId.toLowerCase() === unitId.toLowerCase()
    );
  }
}
